import { AfterViewInit, Component, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { plainToClass } from 'class-transformer';

import { environment } from 'environments/environment';

import { ConstantsService } from 'app/services/constants-service';
import { SidebarTasksService, SidebarTasksTab } from 'app/services/sidebar-tasks.service';

import { ClientHeaderService } from 'app/clients/client/header/client-header.service';
import { PersonHeaderService } from 'app/person/header/person-header.service';
import { EntityDetailsService } from 'app/entities/details/entity-details.service';

import { EntitySelection } from 'app/shared/models/entity-selection.model'
import { EntityIdDetails, EntityType } from 'app/shared/models/entity-id-details.model';

import { OrganisationDetails } from 'app/shared/models/org-details.model';
import { Person } from 'app/shared/models/person.model';

import { EntityOtherService } from './other/entity-other.service';
import {CookieService} from "../services/cookie.service";
import { EntityRouteService } from 'app/entities/entity-route.service';

@Component({
  templateUrl: './entity-dossier.component.html'
})

export class EntityDossierComponent implements AfterViewInit, OnDestroy {
  subscription: any;
  selectedEntityId: string;
  selectedEntityType: string;
  entityIdDetails: EntityIdDetails;
  organisationDetails: OrganisationDetails;
  personDetails: Person;
  isClient: boolean;
  private isLegacyRoute: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public sidebarTasksService: SidebarTasksService,
    private clientHeaderService: ClientHeaderService,
    private personHeaderService: PersonHeaderService,
    private entityDetailsService: EntityDetailsService,
    private entityOtherService: EntityOtherService,
    private cdr: ChangeDetectorRef,
    private cookieService: CookieService,
    private entityRouteService: EntityRouteService
  ) {
    // Set the default Iframe URL
    this.subscription = this.route
      .params
      .subscribe(params => {
        const activeTab = this.getActiveTabFromUrl(this.router.url);

        // Canonical route: /entity/:entityId/...
        if (params['entityId']) {
          this.isLegacyRoute = false;
          this.selectedEntityId = params['entityId'];

          // keep cookie in canonical form for other screens (e.g. tasks)
          this.cookieService.set(ConstantsService.ENT_CUR_ID, this.selectedEntityId);

          this.entityRouteService.getEntityRouteDetails(params['entityId'])
            .subscribe(routeResponse => {
              const routeDetails = routeResponse && routeResponse.arrResult ? routeResponse.arrResult : routeResponse;
              const entityType = routeDetails.entityType;
              const underlyingId = routeDetails.underlyingId;

              this.cookieService.set(ConstantsService.ENT_CUR_TYPE, entityType);

              if (entityType === 'org') {
                this.clientHeaderService.getOrganisationDetails(underlyingId)
                  .subscribe(response => {
                    this.setHeaderData('Org', response, activeTab);
                    this.sidebarTasksService.setEntityId(response.organisationVO.entityId);
                  });
              } else {
                this.personHeaderService.getPerson(underlyingId)
                  .subscribe(response => {
                    this.setHeaderData('Person', response.arrResult, activeTab);
                    this.sidebarTasksService.setEntityId(response.arrResult.entityId);
                  });
              }
            });

          return;
        }

        // Legacy route: /entity/:type/:id/...
        this.isLegacyRoute = true;
        this.selectedEntityType = params['type'];
        this.selectedEntityId = params['id'];

        if (params['type'] === 'org') {
          this.clientHeaderService.getOrganisationDetails(params['id'])
            .subscribe(response => {
              this.setHeaderData('Org', response, activeTab);
              this.sidebarTasksService.setEntityId(response.organisationVO.entityId);
            });
        } else {
          this.personHeaderService.getPerson(params['id'])
            .subscribe(response => {
              this.setHeaderData('Person', response.arrResult, activeTab);
              this.sidebarTasksService.setEntityId(response.arrResult.entityId);
            });
        }
      });
  }

  private getActiveTabFromUrl(url: string): string {
    const pathOnly = (url || '').split('?')[0];
    const segments = pathOnly.split('/').filter(Boolean);

    // legacy: /entity/:type/:id/:tab
    if (segments.length >= 4 && segments[0] === 'entity' && (segments[1] === 'org' || segments[1] === 'person')) {
      return segments[3] || 'details';
    }

    // canonical: /entity/:entityId/:tab
    if (segments.length >= 3 && segments[0] === 'entity') {
      return segments[2] || 'details';
    }

    return 'details';
  }

  private redirectLegacyRouteToCanonical(entityId: number | string, activeTab: string) {
    if (!this.isLegacyRoute) {
      return;
    }
    const tab = activeTab || 'details';
    this.router.navigate(['/entity', entityId, tab], { replaceUrl: true });
  }

  setHeaderData(entityType, response, activeTab: string = 'details') {
    this.selectedEntityType = entityType;
    this.isClient = false;
    this.cdr.detectChanges();
    this.entityIdDetails = new EntityIdDetails();

    if (this.selectedEntityType === 'Org') {
      this.organisationDetails = response;
      this.entityIdDetails.entityType = EntityType.ORGANISATION;
      if (response.organisationVO) {
        this.entityIdDetails.entityId = response.organisationVO.entityId;
        this.entityIdDetails.orgId = response.organisationVO.id;
      }
      if (response.clientVO) {
        this.isClient = true;
        this.entityIdDetails.clientId = response.clientVO.id;
        this.entityIdDetails.entityType = EntityType.CLIENT;
      }
      this.clientHeaderService.organisationDetails(this.organisationDetails);
    }

    if (this.selectedEntityType === 'Person') {
      this.personDetails = response;
      this.entityIdDetails.entityType = EntityType.PERSON;
      this.entityIdDetails.entityId = response.entityId;
      this.entityIdDetails.personId = response.id;
      this.personHeaderService.setPersonDetails(this.personDetails);
    }

    if (this.entityIdDetails && this.entityIdDetails.entityId) {
      this.selectedEntityId = String(this.entityIdDetails.entityId);
    }

    this.entityDetailsService.setEntityIdDetails(this.entityIdDetails);
    this.entityOtherService.setEntityId(this.entityIdDetails);

    // Persist canonical entity selection for other flows (e.g. tasks)
    if (this.entityIdDetails && this.entityIdDetails.entityId) {
      this.cookieService.set(ConstantsService.ENT_CUR_ID, String(this.entityIdDetails.entityId));
      this.cookieService.set(ConstantsService.ENT_CUR_TYPE, this.selectedEntityType === 'Person' ? 'person' : 'org');
    }

    // If user entered via legacy route, normalize the URL in-place.
    if (this.entityIdDetails && this.entityIdDetails.entityId) {
      this.redirectLegacyRouteToCanonical(this.entityIdDetails.entityId, activeTab);
    }
  }

  entitySelectionEvent(event) {
    if (event.origin !== ConstantsService.TRUST_URL
      && event.origin !== ConstantsService.LOCAL_TOMCAT_HOST
      && event.origin !== ConstantsService.LOCAL_ANGULAR_HOST) {
      return;
    }

    try {
      const entitySelection = plainToClass(EntitySelection, JSON.parse(event.data) as Object);

      this.router.navigate(['/entity', entitySelection.entityId, 'details']);
    } catch (e) {
      // Swallow exceptions
    }
  }

  toggleSidebar() {
    this.sidebarTasksService.toggleSidebar();
  }

  toggleTab(tabNumber: Number) {
    if (tabNumber === 0) {
      this.sidebarTasksService.showTab(SidebarTasksTab.MyTasks);
    } else {
      this.sidebarTasksService.showTab(SidebarTasksTab.OtherTasks);
    }
  }

  ngAfterViewInit() {
    if (window.addEventListener) {
      window.addEventListener('message', this.entitySelectionEvent.bind(this), false);
    } else {
      (<any>window).attachEvent('onmessage', this.entitySelectionEvent.bind(this));
    }
  }

  ngOnDestroy() {
    window.removeEventListener('message');

    // ensure that task panel gets closed when closing entity dossier.
    this.sidebarTasksService.ensureSidebarIsClosed();
  }
}



// WEBPACK FOOTER //
// ./src/app/entities/entity-dossier.component.ts